import { gql, useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import styled from '../../../style/styled';
import { Button } from '../../../components/Button';
import { ErrorText } from '../../../styledComponents/ErrorText';
import { Input } from '../../../styledComponents/Input';
import { Label } from '../../../styledComponents/Label';
import {
  EXPLORE_WEB_CreateTourPlan as Data,
  EXPLORE_WEB_CreateTourPlanVariables as Variables,
} from './__generated__/EXPLORE_WEB_CreateTourPlan';
import { captureButtonPress } from '../../../features/analytics';
import { UNKNOWN_ERROR } from '../../../consts';
import { captureInSentry } from '../../../App/config/reporting/captureInSentry';

export const CREATE_TOUR_PLAN_MUTATION = gql`
  mutation EXPLORE_WEB_CreateTourPlan($input: CreateTourPlanInput!) {
    result: createTourPlan(input: $input) {
      tourPlan {
        id
      }
      error {
        id
        message
        type
      }
    }
  }
`;

let _variables: Variables | null = null;

const getVariables = () => _variables;

const setVariables = (variables: Variables) => {
  _variables = variables;
};

interface Props {
  onSuccess: () => void;
}

export default function CreatePlanDialog({ onSuccess }: Props) {
  const [name, setName] = useState<string>('');
  const [error, setError] = useState<Error | null>(null);

  const [createTourPlan, { loading }] = useMutation<Data, Variables>(
    CREATE_TOUR_PLAN_MUTATION,
    {
      onCompleted: ({ result }) => {
        const { error } = result;

        if (error) {
          const errorMessage = error?.message || UNKNOWN_ERROR;

          captureInSentry(errorMessage, { variables: getVariables() });

          setError(new Error(errorMessage));

          toast.error(`Creating tour plan failed. ${errorMessage}`);
        } else {
          onSuccess();
        }
      },
      onError: (error) => {
        Sentry.withScope((scope) => {
          scope.setExtra('variables', getVariables());
          Sentry.captureException(error);
        });

        toast.error(
          `Creating tour plan failed. ${error?.message || UNKNOWN_ERROR}`
        );
      },
    }
  );

  const handleSubmit = () => {
    captureButtonPress({
      page: window.location.pathname,
      buttonName: 'CreatePlan',
    });

    if (name.trim().length === 0) {
      setError(new Error('Please specify a name for your tour plan.'));
    } else {
      // call mutation with variables
      const variables: Variables = { input: { name } };

      setVariables(variables);

      createTourPlan({ variables });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  return (
    <Container>
      <Section>
        <Title>Create new tour plan</Title>
      </Section>

      <Section>
        <p>Add places you find to your Plan and see them all on a handy map.</p>
      </Section>

      <Section>
        <Label htmlFor="name">Name of the Tour Plan</Label>
        <Input
          autoFocus
          id="name"
          name="name"
          type="text"
          disabled={loading}
          value={name}
          onChange={handleInputChange}
          maxLength={50}
          data-testid="name-input-field"
        />
        <ErrorText>{error ? error.message : ''}</ErrorText>
      </Section>

      <Section>
        <ButtonContainer>
          <Button disabled={loading} onClick={handleSubmit}>
            Create tour plan
          </Button>
        </ButtonContainer>
      </Section>
    </Container>
  );
}

export const Section = styled.section`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const Container = styled.div`
  padding: 8px;
  width: 300px;
`;

export const Title = styled.h2``;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;
